import { Link, NavLink, Outlet, useParams } from 'react-router-dom'
import { Suspense } from 'react'
import {
  HELP_CATEGORY_ORDER,
  listHelpCategoriesWithArticles,
  useHelpArticlesReady,
  type HelpCategoryId,
} from '@/content/help'
import { useTranslation, useAppUiI18n } from '@/i18n/useTranslation'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

function categoryLabel(t: (key: string) => string, id: HelpCategoryId): string {
  return t(`marketing.help.categories.${id}`)
}

export default function HelpLayout() {
  const { t } = useTranslation('common')
  const { locale } = useAppUiI18n()
  const { category } = useParams()
  const helpReady = useHelpArticlesReady()
  const categories = helpReady ? listHelpCategoriesWithArticles(locale) : []

  return (
    <div className="marketing-surface border-t border-[var(--mq-ink)]/5 bg-[var(--mq-sky)]/40">
      <div className="mx-auto flex max-w-7xl flex-col gap-6 px-4 py-8 sm:px-6 lg:flex-row lg:gap-8 lg:px-8 lg:py-10">
        <aside className="w-full shrink-0 lg:w-44">
          <Link
            to="/help"
            className="mq-display text-xl font-bold tracking-tight text-[var(--mq-ink)] hover:text-[var(--mq-accent)]"
          >
            {t('marketing.help.title')}
          </Link>
          <p className="mt-1 text-xs text-[var(--mq-ink)]/55">{t('marketing.help.sidebarHint')}</p>
          <nav className="mt-3 space-y-0.5" aria-label={t('marketing.help.categoriesNav')}>
            {HELP_CATEGORY_ORDER.filter((id) => categories.includes(id)).map((id) => (
              <NavLink
                key={id}
                to={`/help/${id}`}
                className={({ isActive }) =>
                  [
                    'block rounded-md px-2.5 py-1.5 text-[0.8125rem] font-medium leading-snug transition',
                    isActive || category === id
                      ? 'bg-[var(--mq-accent)]/10 text-[var(--mq-accent-deep)]'
                      : 'text-[var(--mq-ink)]/65 hover:bg-white/70 hover:text-[var(--mq-ink)]',
                  ].join(' ')
                }
              >
                {categoryLabel(t, id)}
              </NavLink>
            ))}
            <NavLink
              to="/help/contact"
              className={({ isActive }) =>
                [
                  'mt-3 block rounded-md border-t border-[var(--mq-ink)]/10 px-2.5 pb-1.5 pt-3 text-[0.8125rem] font-medium leading-snug transition',
                  isActive
                    ? 'bg-[var(--mq-accent)]/10 text-[var(--mq-accent-deep)]'
                    : 'text-[var(--mq-ink)]/65 hover:bg-white/70 hover:text-[var(--mq-ink)]',
                ].join(' ')
              }
            >
              {t('marketing.help.contactNav')}
            </NavLink>
          </nav>
        </aside>
        <div className="min-w-0 flex-1">
          {helpReady ? (
            <Suspense
              fallback={
                <div className="flex min-h-[30vh] items-center justify-center">
                  <LoadingSpinner />
                </div>
              }
            >
              <Outlet />
            </Suspense>
          ) : (
            <div className="flex min-h-[30vh] items-center justify-center">
              <LoadingSpinner />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
